/**
 * VerifyEmail.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import './Login.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { updateCurrentUser } = useAuth();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return undefined;
    }

    const ac = new AbortController();
    let cancelled = false;

    const verifyEmail = async () => {
      try {
        const API_URL = '/api';
        const url = `${API_URL}/users/verify-email/${encodeURIComponent(token)}`;
        if (process.env.NODE_ENV === 'development') {
          console.log('[VerifyEmail] Verifying:', url);
        }

        const response = await fetch(url, { signal: ac.signal });
        const data = await response.json();
        if (cancelled) return;

        if (response.ok && data.success) {
          setStatus('success');
          setMessage('Your email has been verified successfully! 🎉');
          showSuccess('Email verified successfully! You are now logged in.');
          
          // Automatically log the user in using the token and user info from verification
          if (data.token && data.user) {
            try {
              // Store token and user data (same as login flow)
              localStorage.setItem('token', data.token);
              sessionStorage.setItem('currentUser', JSON.stringify(data.user));
              
              // Update current user state in AuthContext
              updateCurrentUser(data.user);
              window.dispatchEvent(new Event('storage'));
            } catch (error) {
              console.error('Error logging in user after verification:', error);
              showError('Email verified but login failed. Please sign in manually.');
            }
          } else {
            // Fallback: Update existing session if user was already logged in
            const currentUserStr = sessionStorage.getItem('currentUser');
            if (currentUserStr) {
              try {
                const currentUser = JSON.parse(currentUserStr);
                currentUser.email_verified = true;
                sessionStorage.setItem('currentUser', JSON.stringify(currentUser));
                updateCurrentUser(currentUser);
                window.dispatchEvent(new Event('storage'));
              } catch (error) {
                console.error('Error updating session user data:', error);
              }
            }
          }
        } else {
          const code = data.code || '';
          let userMessage = data.error || data.message || 'Verification failed. Please try again.';

          if (code === 'superseded') {
            userMessage =
              'This link was replaced by a newer verification email. Open the most recent email, or use Resend Email while signed in.';
          } else if (code === 'expired') {
            userMessage =
              'This verification link has expired. Sign in and use Resend Email to get a new link (valid 24 hours).';
          } else if (code === 'used') {
            userMessage =
              'This link was already used. Sign in, or use Resend Email if your account is still unverified.';
          } else if (code === 'invalid') {
            userMessage =
              'This verification link is invalid. Sign in and use Resend Email to get a new link.';
          }

          setStatus('error');
          setMessage(userMessage);
          showError(userMessage);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (cancelled) return;
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(`❌ Network error: ${error.message}. Please check your internet connection and try again.`);
        showError('Verification error - please try again');
      }
    };

    verifyEmail();
    return () => {
      cancelled = true;
      ac.abort();
    };
    // Only `token`: showSuccess/showError/updateCurrentUser identities can change after login and
    // would re-run this effect → infinite verify requests and a flashing UI.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [token]);

  return (
    <div className="login-container verification-page">
      <div className="login-form verification-form">
        <h2>Email Verification</h2>
        
        <div className={`verification-status ${status}`}>
          {status === 'verifying' && (
            <LoadingSpinner size="medium" message="Verifying your email..." />
          )}
          
          {status === 'success' && (
            <div className="success-icon-large">✓</div>
          )}
          
          {status === 'error' && (
            <div className="error-icon-large">✗</div>
          )}
          
          <p className="status-message">{message}</p>
        </div>

        {status === 'success' && (
          <div className="verification-success-content">
            <div className="profile-encouragement-banner">
              <h3>🎉 Complete Your Profile</h3>
              <p>Take a moment to fill out your profile information. This will make future checkouts faster and ensure we can deliver your orders correctly.</p>
              <ul className="profile-benefits">
                <li>✓ Faster checkout experience</li>
                <li>✓ Accurate shipping addresses</li>
                <li>✓ Order confirmations sent to the right email</li>
                <li>✓ Personalized shopping experience</li>
              </ul>
            </div>
            <div className="action-buttons">
              <button 
                onClick={() => navigate('/profile')}
                className="verify-profile-button primary-action"
              >
                Complete My Profile →
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="verify-success-button"
              >
                Go to Sign In
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="action-buttons">
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '1rem' }}>
              Verified in your mail app but still see this banner in Safari/Chrome? Sign in here in your normal browser after verifying.
            </p>
            <button onClick={() => navigate('/login')} className="verify-success-button">
              Go to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
